<template>
  <div class="snapshots-gallery">
    <div
      v-for="(snapshot, index) in snapshots"
      :key="`snapshot-${snapshot.key}`"
      class="snapshots-gallery__item"
    >
      <img
        v-if="snapshot.url && !isEmpty(snapshot.key)"
        :src="snapshot.url"
        class="snapshots-gallery__img cursor-pointer"
        loading="lazy"
        v-b-modal.image_modal
        @click="openImageModal(index)"
        @load="markReady(snapshot.key)"
        @error="markEmpty(snapshot.key)"
      />
      <feather-icon
        v-if="isEmpty(snapshot.key)"
        icon="ImageIcon"
        size="20"
        class="snapshots-gallery__state"
        v-b-tooltip.hover
        :title="$t('alarm.imageNotAvailable')"
      />
      <b-spinner
        v-else-if="isLoading(snapshot.key)"
        small
        variant="primary"
        class="snapshots-gallery__state"
      ></b-spinner>
      <span class="snapshots-gallery__sequence">{{ index + 1 }}</span>
    </div>
  </div>
</template>

<script>
import { computed, ref, set, watch } from "@vue/composition-api";
import store from "@/store";

export default {
  name: "SnapshotsGallery",
  props: {
    images: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    // falsy = still loading, "ready" | "empty"
    const pictureState = ref({});

    // Keyed by sequence so a batch arriving later cannot shift the state of the
    // snapshots already rendered
    const snapshots = computed(() =>
      props.images.map((image, index) => ({
        key: image.sequence != null ? image.sequence : index,
        url: image.url,
      }))
    );

    const isEmpty = (key) => pictureState.value[key] === "empty";
    const isLoading = (key) => !pictureState.value[key];
    const markReady = (key) => set(pictureState.value, key, "ready");
    // Presigned urls expire, and there is no endpoint to re-sign a single one:
    // a broken picture shows the empty state until the next preview
    const markEmpty = (key) => set(pictureState.value, key, "empty");

    // The whole list travels to the modal so its arrows walk the gallery
    const openImageModal = (index) => {
      store.dispatch("image_modal/imageList", snapshots.value.map(({ url }) => ({ url })));
      store.dispatch("image_modal/imageIndex", index);
    };

    watch(snapshots, (value, oldValue) => {
      // A new preview replaces the whole list: drop the state of the old urls
      if (!value.length || value.length < oldValue.length) pictureState.value = {};
    });

    return {
      snapshots,
      isEmpty,
      isLoading,
      markReady,
      markEmpty,
      openImageModal,
    };
  },
};
</script>

<style lang="scss" scoped>
.snapshots-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 6px;
  max-width: 500px;
  margin: 0 auto 15px;
}

.snapshots-gallery__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 0.25rem;
  background-color: #0c0c0c;
}

.snapshots-gallery__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.06);
  }
}

.snapshots-gallery__state {
  position: absolute;
  opacity: 0.6;
}

.snapshots-gallery__sequence {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 0 0.25rem;
  font-size: 0.7rem;
  line-height: 1.4;
  border-top-left-radius: 0.25rem;
  background-color: rgba(0, 0, 0, 0.55);
  text-shadow: 1px 1px #000;
  pointer-events: none;
}
</style>
